/**
 * MapScene — everything built for one GameMap: baked fields, the static layers and the dynamic
 * layers that draw onto it. Created when a map arrives, destroyed when it changes or the
 * renderer goes away. Shared, map-independent resources (atlas, noise textures) live in
 * `SharedResources` and outlive scenes.
 */
import { type GameMap, type HexGrid, isWater, NO_FACTION } from '@friction/engine';
import type { Texture } from 'pixi.js';
import { type Glyph, type MarkerGlyphs, markerGlyphs, tagGlyphs } from './atlas/glyphs';
import { makeNoiseTexture, makePaperTexture } from './bake/noise';
import { bakeTerrain, type TerrainFields } from './bake/terrainFields';
import { Atlas } from './gfx/atlas';
import { BattlesLayer } from './layers/battles';
import { BordersLayer } from './layers/borders';
import { FeaturesLayer } from './layers/features';
import { LabelsLayer } from './layers/labels';
import { OrdersLayer } from './layers/orders';
import { OverlayLayer } from './layers/overlay';
import { type FieldTextures, TerrainLayer, uploadFields } from './layers/terrain';
import { UnitsLayer } from './layers/units';
import { IceLayer } from './layers/weather';
import { type SheetLayout, sheetLayout } from './sheet';

export class SharedResources {
  readonly atlas = new Atlas(2048);
  readonly noise: Texture = makeNoiseTexture();
  readonly paper: Texture = makePaperTexture();
  readonly glyphs: MarkerGlyphs = markerGlyphs(this.atlas);
  readonly tags: Map<string, Glyph> = tagGlyphs(this.atlas);

  destroy(): void {
    this.atlas.destroy();
    this.noise.destroy(true);
    this.paper.destroy(true);
  }
}

export class MapScene {
  readonly map: GameMap;
  readonly grid: HexGrid;
  readonly sheet: SheetLayout;
  readonly fields: TerrainFields;
  readonly fieldTex: FieldTextures;
  readonly terrain: TerrainLayer;
  readonly features: FeaturesLayer;
  /** Frozen river stretches (between the features and the overlay's snow and fog). */
  readonly ice: IceLayer;
  readonly overlay: OverlayLayer;
  readonly borders: BordersLayer;
  readonly labels: LabelsLayer;
  readonly units: UnitsLayer;
  readonly orders: OrdersLayer;
  readonly battles: BattlesLayer;
  /** Bake time in ms (reported by the lab). */
  readonly bakeMs: number;

  constructor(map: GameMap, grid: HexGrid, shared: SharedResources, resolution: number) {
    const t0 = performance.now();
    this.map = map;
    this.grid = grid;
    this.sheet = sheetLayout(grid);
    this.fields = bakeTerrain(map, grid, this.sheet.neat);
    this.fieldTex = uploadFields(this.fields);
    const land = (h: number) => !isWater(map.terrain[h]);
    const { atlas, glyphs, noise, paper, tags } = shared;
    this.terrain = new TerrainLayer(map, grid, this.sheet, this.fieldTex, noise, paper);
    this.features = new FeaturesLayer(map, grid, this.fields, atlas);
    this.ice = new IceLayer();
    // Neutral countries of a real-world theatre: closed land, keyed by its region (one per country).
    const neutral = (h: number) => (map.initialOwner[h] === NO_FACTION ? Math.max(0, map.region[h]) : -1);
    this.overlay = new OverlayLayer(grid, land, this.sheet, this.fieldTex, noise, neutral);
    this.borders = new BordersLayer(grid, land, glyphs, atlas.texture);
    this.labels = new LabelsLayer(map, grid, this.sheet, glyphs, atlas.texture, resolution);
    this.units = new UnitsLayer(grid, atlas, glyphs, tags, atlas.texture);
    this.orders = new OrdersLayer(grid, glyphs, atlas.texture);
    this.battles = new BattlesLayer(map, grid, glyphs, atlas.white, atlas.texture);
    this.bakeMs = performance.now() - t0;
  }

  destroy(): void {
    this.terrain.destroy();
    this.features.destroy();
    this.ice.destroy();
    this.overlay.destroy();
    this.borders.destroy();
    this.labels.destroy();
    this.units.destroy();
    this.orders.destroy();
    this.battles.destroy();
    this.fieldTex.destroy();
  }
}
